import { LegalPageLayout, P, UL, LI, Strong, ContactLink } from './LegalPageLayout';

/**
 * Terms of Service for PlyEZ (plyez.com) — the binding B2B SaaS agreement
 * between the Company (a Japan 株式会社) and each Customer. Covers the
 * service, accounts, billing, AI features, data ownership, the payment-
 * rail / file-orchestration disclaimers specific to a finance product,
 * warranties, liability, and Japan governing law (Tokyo District Court).
 * Effective 2026-06-03. This reflects PlyEZ's current operating practices;
 * we recommend reviewing it with your own counsel.
 */
export function TermsPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.terms"
      lastUpdated="2026-06-03"
      lead={
        <>
          These Terms of Service (the &ldquo;Terms&rdquo;) govern access to and use of{' '}
          <Strong>PlyEZ</Strong> — an AI-native back-office automation platform for
          accounts payable and receivable, expense, payments, ledger, Japanese
          compliance, and cross-border workflows — operated by{' '}
          <Strong>[registered company name]</Strong>, a Japanese stock company
          (株式会社) (the &ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).
          By signing up for, accessing, or paying for the Service, you accept these
          Terms on behalf of yourself and the organization you represent. If you do
          not agree, do not use the Service.
        </>
      }
      sections={[
        {
          id: 'definitions',
          title: '1. Definitions',
          body: (
            <>
              <UL>
                <LI><Strong>&ldquo;Service&rdquo;</Strong> — the plyez.com website, the PlyEZ web application, the PlyEZ API, and all related software, features, and documentation we make available.</LI>
                <LI><Strong>&ldquo;Customer&rdquo;</Strong> — the organization that registers for the Service and is responsible for billing, administration, and its data. &ldquo;You&rdquo; refers to the Customer and its Authorized Users.</LI>
                <LI><Strong>&ldquo;Authorized User&rdquo;</Strong> — an individual the Customer permits to use the Service under its account (employees, contractors, advisors).</LI>
                <LI><Strong>&ldquo;Customer Data&rdquo;</Strong> — all data, documents, and content the Customer or its Authorized Users submit to or generate within the Service (invoices, vendor records, expense receipts, journal entries, bank-feed lines, payment instructions, AI prompts and outputs, and similar).</LI>
                <LI><Strong>&ldquo;AI Features&rdquo;</Strong> — functionality that uses machine-learning models to extract, classify, draft, match, or suggest, including document capture, vendor matching, ledger coding, and semantic search.</LI>
                <LI><Strong>&ldquo;Sub-processor&rdquo;</Strong> — a third party we engage to process Customer Data, listed at <ContactLink to="/legal/subprocessors">/legal/subprocessors</ContactLink>.</LI>
                <LI><Strong>&ldquo;Order&rdquo;</Strong> — the plan selection, quote, or order form that sets out the subscribed tier, fees, and term.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'service',
          title: '2. The Service',
          body: (
            <>
              <P>
                The Service helps organizations automate back-office finance work:
                capturing and coding invoices and receipts, managing vendors and
                approvals, orchestrating domestic and cross-border payments,
                maintaining a ledger and audit trail, and producing records that
                support Japanese tax and bookkeeping obligations.
              </P>
              <P>
                We may improve, modify, or add features over time. Material reductions
                in core functionality during a paid term will be communicated in
                advance. Features released as &ldquo;beta&rdquo;, &ldquo;preview&rdquo;,
                or &ldquo;early access&rdquo; are provided as-is, may change or be
                withdrawn, and are excluded from any service-level commitment.
              </P>
            </>
          ),
        },
        {
          id: 'accounts',
          title: '3. Accounts, eligibility, and Authorized Users',
          body: (
            <>
              <P>
                The Service is intended for business use by organizations and their
                personnel. Each Authorized User must be at least 18 years old and
                authorized to act on the Customer&rsquo;s behalf. You must provide
                accurate registration information and keep it current.
              </P>
              <P>
                The Customer is responsible for all activity under its account,
                including the acts and omissions of its Authorized Users, for
                maintaining the confidentiality of credentials, and for promptly
                disabling access for personnel who should no longer have it.
                Credentials must not be shared; each individual must use their own
                account. Notify us at{' '}
                <a className="text-accent-700 underline" href="mailto:security@plyez.com">security@plyez.com</a>{' '}
                of any suspected unauthorized access.
              </P>
            </>
          ),
        },
        {
          id: 'billing',
          title: '4. Plans, fees, billing, and renewal',
          body: (
            <>
              <P>
                Paid plans are billed in advance per the applicable Order, with
                subscription fees charged for the selected billing period (monthly or
                annual). Subscriptions renew automatically for successive periods of
                the same length until cancelled. Card and subscription billing is
                processed by <Strong>Stripe</Strong>; by subscribing you authorize us
                and Stripe to charge your payment method for all fees due.
              </P>
              <UL>
                <LI><Strong>Metered usage.</Strong> AI Features and certain payment-rail actions may be metered and billed in arrears in addition to subscription fees. Workspaces can set a monthly AI spend cap; when reached, AI Features pause until the next period or the cap is raised.</LI>
                <LI><Strong>Proration.</Strong> Mid-cycle upgrades and added seats are prorated for the remainder of the current period. Downgrades take effect at the next renewal.</LI>
                <LI><Strong>Taxes.</Strong> Fees are exclusive of taxes. You are responsible for applicable consumption tax (消費税), VAT, GST, sales, and withholding taxes. For Japan-domiciled customers we issue a qualified invoice (適格請求書) bearing the Company&rsquo;s registration number.</LI>
                <LI><Strong>Late or failed payment.</Strong> If a charge fails, we may retry and may suspend access until amounts are paid. Undisputed overdue amounts may accrue interest at the lower of 1.5% per month or the maximum permitted by law.</LI>
                <LI><Strong>Cancellation and refunds.</Strong> You may cancel at any time from Settings → Billing; cancellation takes effect at the end of the current paid period and stops future renewals. Except where required by law or expressly stated in an Order, fees already paid are non-refundable and we do not provide partial-period refunds.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'ai',
          title: '5. AI Features',
          body: (
            <>
              <P>
                AI Features use third-party large-language and machine-learning models
                reached through an AI inference gateway (<Strong>OpenRouter</Strong>)
                that routes requests to underlying model providers, and a vector search
                service (<Strong>Qdrant</Strong>) for semantic matching. We do not use
                Customer Data to train our own or third parties&rsquo; foundation
                models, and we contract for zero-retention handling with our AI
                Sub-processors where available.
              </P>
              <P>
                <Strong>AI outputs are assistive, not authoritative.</Strong> They are
                probabilistic and may be incomplete, inaccurate, or out of date.
                Because PlyEZ is a finance product, you remain solely responsible for
                reviewing and verifying every AI-generated result — including extracted
                invoice fields, vendor and tax classifications, suggested journal
                postings, payment amounts and counterparties, and any drafted
                correspondence — before approving, posting, paying, or filing. You must
                not treat AI output as professional advice, and we are not liable for
                losses arising from reliance on unreviewed AI output. Enterprise
                customers may pin AI routing to a single provider or region for
                data-residency posture.
              </P>
            </>
          ),
        },
        {
          id: 'use',
          title: '6. Acceptable use',
          body: (
            <>
              <P>
                You agree to use the Service in compliance with these Terms, our{' '}
                <ContactLink to="/legal/aup">Acceptable Use Policy</ContactLink>, and
                all applicable laws and regulations. The Acceptable Use Policy is
                incorporated by reference; violating it is a breach of these Terms and
                may result in suspension or termination. Without limiting that policy,
                you will not upload data you lack the right to process, circumvent
                usage or rate limits or AI spend caps, reverse-engineer or scrape the
                Service beyond documented API endpoints, or use the Service to launder
                funds, evade sanctions, or facilitate fraud.
              </P>
            </>
          ),
        },
        {
          id: 'customer-data',
          title: '7. Customer Data and intellectual property',
          body: (
            <>
              <P>
                <Strong>You own your Customer Data.</Strong> As between the parties,
                the Customer retains all right, title, and interest in Customer Data.
                We claim no ownership of it. You grant the Company a worldwide,
                non-exclusive, royalty-free license to host, copy, transmit, process,
                and display Customer Data, and to create back-ups and derived metadata,
                solely as needed to provide, secure, and support the Service and as
                instructed by you. Our processing of personal data within Customer Data
                is governed by our{' '}
                <ContactLink to="/legal/dpa">Data Processing Agreement</ContactLink>{' '}
                and <ContactLink to="/legal/privacy">Privacy Policy</ContactLink>.
              </P>
              <P>
                The Service, including its software, models, interfaces, documentation,
                and the PlyEZ name and logos, is owned by the Company and its licensors
                and protected by intellectual-property laws. Subject to these Terms, we
                grant you a non-exclusive, non-transferable, non-sublicensable right to
                access and use the Service for your internal business purposes during
                the subscription term. We may use aggregated and de-identified data
                that does not identify you or any individual to operate and improve the
                Service. You may export your Customer Data at any time during the term
                via the in-app export tools and supported accounting connectors.
              </P>
            </>
          ),
        },
        {
          id: 'confidentiality',
          title: '8. Confidentiality',
          body: (
            <>
              <P>
                Each party may receive non-public information of the other that is
                marked or reasonably understood to be confidential (&ldquo;Confidential
                Information&rdquo;), including Customer Data, the Service&rsquo;s
                non-public features, security documentation, and pricing. The receiving
                party will use Confidential Information only to perform under these
                Terms, protect it with at least reasonable care, and disclose it only to
                personnel and Sub-processors with a need to know who are bound by
                comparable obligations.
              </P>
              <P>
                Confidentiality obligations do not apply to information that is or
                becomes public through no fault of the receiving party, was lawfully
                known without obligation before disclosure, or is independently
                developed. A party may disclose Confidential Information if compelled by
                law, giving reasonable advance notice where permitted so the other party
                can seek protective treatment.
              </P>
            </>
          ),
        },
        {
          id: 'third-party',
          title: '9. Third-party services and Sub-processors',
          body: (
            <>
              <P>
                We engage vetted Sub-processors to deliver the Service — including
                Cloudflare R2 (object storage), Amazon SES (transactional email),
                Stripe (billing and card issuing), Wise (cross-border payouts),
                OpenRouter (AI inference routing), Qdrant (vector search), and Hetzner
                (hosting). The current list, with purpose and region, is at{' '}
                <ContactLink to="/legal/subprocessors">/legal/subprocessors</ContactLink>,
                and their handling of personal data is governed by the{' '}
                <ContactLink to="/legal/dpa">DPA</ContactLink>. We give advance notice
                of new or replaced Sub-processors as set out in the DPA.
              </P>
              <P>
                The Service may interoperate with third-party products you choose to
                connect (for example accounting systems, banks, identity providers, a
                Peppol access point, or a timestamping authority). Those products are
                governed by their own terms; we are not responsible for their
                availability, acts, or omissions, and your use of them is at your own
                discretion.
              </P>
            </>
          ),
        },
        {
          id: 'payments',
          title: '10. Payments, files, and the role of the Company',
          body: (
            <>
              <P>
                <Strong>The Company is a software provider, not a bank, money
                transmitter, or deposit-taking institution.</Strong> We do not hold
                customer funds on deposit. Where the Service facilitates payments, we
                orchestrate and format instructions and route them through regulated
                partners — for example Stripe for card and billing, Wise for
                cross-border payouts, and, where enabled, GMO Payment Gateway for
                Japan-domestic card issuance. Movement of funds is performed by those
                licensed providers under their own terms, and you are responsible for
                meeting their requirements.
              </P>
              <UL>
                <LI><Strong>Bank files.</Strong> For domestic bank transfers, the Service generates payment files (for example 全銀フォーマット) that you review, approve, and upload to or transmit through your own bank. We do not connect to your bank to move money on your behalf unless a specific integration is enabled and contracted; submission to the bank, and the consequences of submission, are your responsibility.</LI>
                <LI><Strong>Accuracy and authorization.</Strong> You are responsible for verifying payee details, amounts, currencies, and that each payment is properly authorized before it is approved or released. We are not liable for losses arising from instructions you approve, including misdirected or duplicate payments resulting from inaccurate inputs.</LI>
                <LI><Strong>Compliance screening.</Strong> Payment partners may screen, delay, or reject transactions to comply with sanctions, AML/CFT, and other legal obligations; such actions are outside our control.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'compliance',
          title: '11. Compliance, tax, and bookkeeping — assistive only',
          body: (
            <>
              <P>
                The Service helps you produce and retain records that support Japanese
                and cross-border obligations — including qualified invoices
                (適格請求書), 7-year electronic document retention under the Electronic
                Books Preservation Act (電子帳簿保存法), approval workflows (稟議), JP
                PINT / Peppol messages, and consumption-tax, VAT, and information-return
                extracts. These are tools to assist your team.
              </P>
              <P>
                <Strong>The Service does not provide legal, tax, accounting, or
                financial advice, and the Company is not your tax agent (税理士),
                accountant, or auditor.</Strong> Determining your obligations, the
                accuracy of records, and the timing and content of filings remains your
                responsibility and that of your qualified professional advisors. We
                recommend reviewing your configuration and outputs with your own
                counsel and accountants; this Service reflects our current practices and
                is not a substitute for professional advice.
              </P>
            </>
          ),
        },
        {
          id: 'warranty',
          title: '12. Warranties and disclaimers',
          body: (
            <>
              <P>
                Each party represents that it has the authority to enter into these
                Terms. We will provide the Service with reasonable skill and care and,
                for Enterprise plans, in accordance with our{' '}
                <ContactLink to="/legal/sla">Service Level Agreement</ContactLink>.
              </P>
              <P>
                Except as expressly stated in these Terms, the Service is provided
                <Strong> &ldquo;AS IS&rdquo;</Strong> and{' '}
                <Strong>&ldquo;AS AVAILABLE&rdquo;</Strong> without warranties of any
                kind, whether express, implied, or statutory, including implied
                warranties of merchantability, fitness for a particular purpose, title,
                and non-infringement. We do not warrant that the Service will be
                uninterrupted or error-free, that AI outputs will be accurate, or that
                the Service will meet every regulatory requirement applicable to your
                business. Some jurisdictions do not allow the exclusion of certain
                warranties; in that case the exclusions apply to the maximum extent
                permitted by law.
              </P>
            </>
          ),
        },
        {
          id: 'liability',
          title: '13. Limitation of liability',
          body: (
            <>
              <P>
                To the maximum extent permitted by applicable law, neither party will be
                liable for any indirect, incidental, special, consequential, exemplary,
                or punitive damages, or for lost profits, revenue, goodwill, or
                anticipated savings, or for loss or corruption of data, even if advised
                of the possibility of such damages.
              </P>
              <P>
                To the maximum extent permitted by law, each party&rsquo;s total
                aggregate liability arising out of or related to these Terms or the
                Service will not exceed the total fees you paid to the Company for the
                Service in the twelve (12) months immediately preceding the event giving
                rise to the claim. These limitations do not apply to a party&rsquo;s
                liability for death or personal injury caused by its negligence, fraud
                or fraudulent misrepresentation, the Customer&rsquo;s payment
                obligations, or either party&rsquo;s indemnification obligations, or to
                any liability that cannot be limited under applicable law.
              </P>
            </>
          ),
        },
        {
          id: 'indemnity',
          title: '14. Indemnification',
          body: (
            <>
              <P>
                The Customer will defend, indemnify, and hold harmless the Company and
                its affiliates, officers, and personnel from and against third-party
                claims, damages, and reasonable costs (including reasonable legal fees)
                arising out of Customer Data, the Customer&rsquo;s use of the Service in
                breach of these Terms or the Acceptable Use Policy, or the
                Customer&rsquo;s violation of law or of third-party rights.
              </P>
              <P>
                The Company will defend the Customer against third-party claims alleging
                that the Service, as provided by us and used in accordance with these
                Terms, infringes that third party&rsquo;s intellectual-property rights,
                and will pay damages finally awarded or amounts in a settlement we
                approve. This obligation does not apply to claims arising from Customer
                Data, modifications not made by us, or combination of the Service with
                products we did not supply. The party seeking indemnity must give prompt
                notice, reasonable cooperation, and control of the defense to the
                indemnifying party.
              </P>
            </>
          ),
        },
        {
          id: 'term',
          title: '15. Term, suspension, and termination',
          body: (
            <>
              <P>
                These Terms apply for as long as you use the Service. Either party may
                terminate for the other&rsquo;s material breach that remains uncured 30
                days after written notice. You may cancel your subscription at any time
                as described in Section 4.
              </P>
              <P>
                We may suspend access immediately where necessary to address a security
                risk, a violation of the Acceptable Use Policy, non-payment, or a legal
                requirement, and will limit the suspension in scope and duration to what
                is reasonably necessary. On termination, your right to use the Service
                ends. We will make Customer Data available for export for 30 days after
                termination, after which we will delete it in the ordinary course,
                except where retention is required by law (including 7-year retention
                under the Electronic Books Preservation Act) or as set out in the DPA.
                Provisions that by their nature should survive termination — including
                ownership, confidentiality, disclaimers, limitation of liability,
                indemnity, and governing law — survive.
              </P>
            </>
          ),
        },
        {
          id: 'changes',
          title: '16. Changes to these Terms',
          body: (
            <>
              <P>
                We may update these Terms to reflect changes in the Service, our
                practices, or the law. For material changes, we will provide at least
                30 days&rsquo; notice via in-app notification and email before they take
                effect. Your continued use of the Service after the effective date
                constitutes acceptance of the updated Terms. The current version and its
                effective date are always available on this page.
              </P>
            </>
          ),
        },
        {
          id: 'general',
          title: '17. General',
          body: (
            <>
              <UL>
                <LI><Strong>Entire agreement.</Strong> These Terms, the Acceptable Use Policy, Privacy Policy, DPA, SLA, and any Order form the entire agreement and supersede prior understandings. For Enterprise customers, a signed Master Service Agreement controls in the event of conflict.</LI>
                <LI><Strong>Assignment.</Strong> You may not assign these Terms without our consent, except to a successor in a merger or sale of substantially all assets. We may assign to an affiliate or successor.</LI>
                <LI><Strong>Force majeure.</Strong> Neither party is liable for delay or failure caused by events beyond its reasonable control.</LI>
                <LI><Strong>Severability and waiver.</Strong> If a provision is unenforceable, the rest remain in effect; failure to enforce a provision is not a waiver.</LI>
                <LI><Strong>Notices.</Strong> Legal notices to us must be sent to <a className="text-accent-700 underline" href="mailto:legal@plyez.com">legal@plyez.com</a>; we may notify you in-app or by email to your account address.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'law',
          title: '18. Governing law and jurisdiction',
          body: (
            <>
              <P>
                These Terms and any dispute arising out of or in connection with them or
                the Service are governed by the laws of Japan, without regard to
                conflict-of-law principles and excluding the United Nations Convention
                on Contracts for the International Sale of Goods. The parties submit to
                the exclusive jurisdiction of the Tokyo District Court (東京地方裁判所)
                as the court of first instance. Where a customer or consumer benefits
                from non-waivable mandatory protections under the law of their place of
                residence, nothing in these Terms removes those protections.
              </P>
            </>
          ),
        },
        {
          id: 'contact',
          title: '19. Contact',
          body: (
            <>
              <P>
                Questions about these Terms can be sent via{' '}
                <ContactLink to="/contact">/contact</ContactLink> or to{' '}
                <a className="text-accent-700 underline" href="mailto:legal@plyez.com">legal@plyez.com</a>.
                Operated by <Strong>[registered company name]</Strong>,{' '}
                <Strong>[registered address]</Strong>, Japan.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}
